/**
 * LEADS — cards, not a spreadsheet.
 *
 * Anthony: "Leads needs to be a card that pops out as well, not a csv row
 * down the page." He is right, and the reason is what the screen is FOR. A
 * job row is a record you scan. A lead is a person you are about to ring,
 * and the things you need in that moment — their number, what they want,
 * where they are, how long they have been waiting — do not fit on one line
 * without becoming a wall of small gray text.
 *
 * So: a grid of cards, and tapping one opens it into a panel with the whole
 * enquiry and the three things you can do about it. The panel is a real
 * dialog, so Escape closes it and the page behind does not scroll away
 * underneath.
 *
 * The three piles are the funnel, not filters. A lead is waiting, booked,
 * or lost — and after a week of silence with no quote raised it is lost
 * whether or not anybody pressed anything. That rule lives in data/leads.ts
 * so the screen and the database agree about it.
 */

import {
  listLeads, countPiles, setLeadStatus, closeAgedOut, hasAgedOut,
  AGED_DAYS, type Lead, type Pile,
  convertToQuote, neverContacted, goneQuiet, clearFlagFor, SPAM_REASON,
} from "../data/leads.ts";
import { STAGE_LABEL, STAGE_HINT, theyDid } from "../domain/leadstage.ts";
import { esc, ago, mount, since } from "../ui/dom.ts";
import { vinOutHtml } from "../ui/vinout.ts";
import { SPANS, spanFrom } from "../data/funnel.ts";
import { api, db } from "../data/client.ts";
import { startLive } from "../data/live.ts";
import { saveLine } from "./lines.ts";
import { shopLabourRate, shopTimezone } from "../data/jobs.ts";
import { outOfArea } from "../domain/outofarea.ts";
import { newJobSheet } from "./newjob.ts";
import { commsForPhone, spoken, type Comm } from "../data/comms.ts";
import { textBackLink } from "./comms.ts";
import { callButtonsHtml, wireCallButtons } from "../ui/callplay.ts";
import { findBarHtml, wireFindBar } from "../ui/findbar.ts";
import { jobPhotos } from "./photos.ts";
import { notesCard } from "./notes.ts";
import type { Role } from "../domain/permissions.ts";
import { shopWords } from "../ui/words.ts";
import { cap } from "../domain/trade.ts";
import { isPpiJob } from "../domain/ppi.ts";
import { maySendToPpiDesk, wireSendToPpiDesk } from "../data/ppi.ts";
import { textsCard, workingBar, wireWorking } from "./ppidesk.ts";

/**
 * A LEAD THAT IS A WINDOW ONTO A PPI DESK REQUEST (source 'ppi-desk',
 * server/tasks/ppi-mirror.ts). Anthony, 2026-10-18: PPI calls "need to go
 * into Supercanic Shop Desk as well, just update as they work together".
 * The PPI desk owns it: this sheet shows the request's step and who is
 * working it, texts the buyer from the PPI line through the request, and
 * offers nothing that would make a second record here (no quote, no car,
 * no Send to PPI Desk, no lost or booked by hand -- the request decides).
 */
const isPpiMirror = (l: Pick<Lead, "source">) => l.source === "ppi-desk";

const PILES: Array<{ key: Pile; label: string }> = [
  { key: "waiting", label: "Waiting" },
  { key: "booked", label: "Booked" },
  { key: "lost", label: "Lost" },
];

let pile: Pile = "waiting";
/** Whether somebody has asked to see past the last week. Reset per visit. */
let openedUp = false;
let open: Lead | null = null;

const DAY = 86400000;

/**
 * OUT OF AREA. Anthony, 2026-10-08: a Corona, CA customer texted Huey's
 * Mobile in North Carolina by mistake and it was quoted. One line on the
 * card and the panel, never a block. See src/domain/outofarea.ts.
 */
let shopZone: string | null = null;
const farFrom = (l: Lead) => outOfArea({
  text: [l.notes, l.service_wanted, l.location,
         l.last_said?.who === "them" ? l.last_said.body : null],
  phone: l.customer_phone,
}, shopZone);
const farHtml = (l: Lead): string => {
  const f = farFrom(l);
  return f ? `<div class="lead-far">${esc(f.why)}</div>` : "";
};

/**
 * A CALL NOTE IS NOT WHAT THEY WANT. `service_wanted` is usually CallRail's
 * own summary — "Rang in. Calling from Hemet, CA. Came in via Google My
 * Business." — which is where the call came from, not what the customer
 * needs. On a tile it is a paragraph of noise above the one line that
 * matters, and it repeats the source pill sitting under it.
 */
const isCallNote = (s: string): boolean =>
  /^(rang in|missed call|dnd|call from|inbound|voicemail)/i.test(s.trim());
const initials = (n: string) =>
  n.trim().split(/\s+/).slice(0, 2).map((w) => w[0] ?? "").join("").toUpperCase() || "?";

function card(l: Lead): string {
  const age = Date.now() - Date.parse(l.created_at);
  const hot = pile === "waiting" && age < DAY;
  const cold = pile === "waiting" && age > 2 * DAY;
  const name = l.customer_name?.trim() || "No name left";
  const aged = hasAgedOut(l);

  return `
    <button class="lead ${hot ? "hot" : ""}${cold ? " cold" : ""}${
      l.flag === "red" || l.call_now ? " callnow" : ""}${
      l.flag_kind === "landline_caller" ? " landline" : ""}${
      l.flag === "orange" ? " waitback" : ""}" data-id="${l.id}">
      <div class="lead-top">
        <span class="avatar">${esc(initials(name))}</span>
        <div class="lead-who">
          <div class="lead-name">${esc(name)}${
            l.dupes && l.dupes > 1
              ? ` <i class="lead-dupes" title="This person is on ${l.dupes} rows. Every call from one number is one card now.">${l.dupes}&times;</i>`
              : ""}</div>
          <!-- THE TRUE SENTENCE, NOT A TRUE-SOUNDING ONE. "Since anyone
               spoke" was counting from our own automatic text, so a lead
               nobody had touched read as if somebody had just dealt with
               it. Who moved it is half the fact. -->
          <div class="lead-age">${pile === "waiting" && l.last_move
            ? (l.last_move.who === "them"
                ? `${theyDid(l.last_move.kind)} ${esc(since(l.last_move.at))}`
                : `we texted ${esc(since(l.last_move.at))}`)
            : pile === "waiting"
              ? `${esc(ago(l.created_at))} waiting`
              : esc(since(l.created_at))}</div>
        </div>
      </div>
      <!-- TWO TEXTS AND NOTHING BACK. A third message is the shop talking
           to itself; a phone call is the shop with a customer. Loud, at the
           top of the card, because the whole point is that it stops
           somebody scrolling past. -->
      <!-- RED AND ORANGE SAY DIFFERENT THINGS. Red is nobody has spoken to
           this person and the machine cannot reach them: a landline that
           rang, or a stranger who ignored two texts. Orange is one of ours
           sitting there waiting on an answer. Flattening the two into one
           color is what made the old flag mean nothing. -->
      <!-- A LANDLINE IS THE PRIORITY CALLBACK and says so in one word.
           Two things turn a lead red and only one of them can never answer
           a text; flattening those into one message is how the urgent one
           gets worked third. -->
      ${l.flag_kind === "landline_caller"
        ? `<div class="lead-call"><b class="lead-landline">LANDLINE</b>
             Call now &mdash; no text can reach them</div>`
        : l.flag === "red" ? `<div class="lead-call">${esc(l.flag_why ?? "Ring them")}</div>`
        : l.flag === "orange" ? `<div class="lead-wait">${
            esc(l.flag_why ?? "Waiting on an answer from us")}</div>`
        : l.call_now ? `<div class="lead-call">Texted twice, no answer &mdash; ring them</div>`
        : ""}
      ${farHtml(l)}

      <!-- WHAT WAS ACTUALLY SAID. Anthony: "Show the last message in the
           tile box."

           A tile that says a lead came in and gives no words is a tile you
           have to open to learn anything. One line of what was actually
           said answers most of what somebody opens it for, and it says WHO
           said it, because "I need a quote on a trans swap" and "sorry we
           missed your call" are not the same tile. -->
      ${l.last_said ? `<div class="lead-said ${l.last_said.who}">
        <i>${l.last_said.who === "them" ? "They said" : "We said"}</i>
        ${esc(l.last_said.body.slice(0, 120))}</div>` : ""}

      ${l.service_wanted && !isCallNote(l.service_wanted)
        ? `<div class="lead-want">${esc(l.service_wanted)}</div>` : ""}
      <div class="lead-foot">
        ${l.customer_phone ? `<span class="lead-tel">${esc(l.customer_phone)}</span>` : `<span class="lead-tel none">no number</span>`}
        <!-- THE CAMPAIGN, not the pipe. "Hem-GMB" rather than "callrail";
             the full name is on hover. See domain/leadcampaign.ts. -->
        ${l.campaign ? `<span class="pill" title="${esc(l.campaign.name)}">${esc(l.campaign.tag)}</span>`
          : l.source ? `<span class="pill">${esc(l.source)}</span>` : ""}
        ${aged ? `<span class="pill s-cancelled">aged out</span>` : ""}
        <!-- WHERE THEY ARE IN THE CAMPAIGN, not what a column says.
             Anthony: "it's confusing because ones that are probably in the
             back campaign say not contacted."

             He was right and the fault was ours. "Not contacted" was read
             off the status column, which only moves when OUR task both sends AND
             updates the row — so a lead the old deployment texted, or one
             a person texted from their phone, said "not contacted" beside
             a red tag saying we had texted twice. The card argued with
             itself. The stage is read from the conversation now. -->
        ${pile === "waiting" && l.stage
          ? `<span class="pill stage-${esc(l.stage)}" title="${
              esc(STAGE_HINT[l.stage])}">${esc(STAGE_LABEL[l.stage])}</span>`
          : ""}
      </div>
    </button>`;
}

function panel(l: Lead): string {
  const name = l.customer_name?.trim() || "No name left";
  const aged = hasAgedOut(l);
  return `
    <div class="sheet-back" id="sheet-back">
      <div class="sheet" role="dialog" aria-modal="true">
        <div class="sheet-head">
          <span class="avatar big">${esc(initials(name))}</span>
          <div>
            <h2>${esc(name)}</h2>
            <div class="quiet">${esc(since(l.created_at))}${l.campaign ? ` &middot; ${esc(l.campaign.name)}` : l.source ? ` &middot; ${esc(l.source)}` : ""}</div>
          </div>
          <button class="sheet-x" id="sheet-x" aria-label="Close">&times;</button>
        </div>

        ${aged ? `<div class="aged-note">No answer in ${AGED_DAYS} days and no quote raised, so this
          counts as lost. Closing it just makes the record say so.</div>` : ""}

        ${farHtml(l)}
        <div class="sheet-body">
          ${isPpiMirror(l) ? `<div class="ppi ppi-mirror" id="ppi-mirror"><p class="quiet">Reading the PPI desk&hellip;</p></div>` : ""}
          <div class="kv"><span>Phone</span><b>${l.customer_phone
            ? `<a href="tel:${esc(l.customer_phone)}">${esc(l.customer_phone)}</a>` : "—"}</b></div>
          <div class="kv"><span>Email</span><b>${esc(l.customer_email ?? "—")}</b></div>
          <div class="kv"><span>Wants</span><b>${esc(l.service_wanted ?? "—")}</b></div>
          ${shopWords().caseFile ? "" : `<div class="kv"><span>Where</span><b>${esc(l.location ?? "—")}</b></div>`}
          ${l.notes ? `<div class="kv"><span>Notes</span><b>${esc(l.notes)}</b></div>` : ""}
          ${l.customer_id ? `<div class="kv"><span>On file</span><b><a href="#/customer/${l.customer_id}">Open their record</a></b></div>` : ""}

          <!-- NOTES ON A LEAD, which is the point Anthony made: "even a
               lead should have this so information could be added". Half of
               what a shop learns arrives before there is a job to hang it
               on, and until now the only place to put it was a single
               notes field that the next person overwrote. -->
          <div class="card" id="lead-notes"></div>

          <!-- ─── WHAT WAS ACTUALLY SAID ────────────────────────────────
               Anthony: "pull ... any text message history from GHL".

               Every text this number has exchanged with the shop already
               sits in comm_events; the card never asked. The one function
               that reads them needs a customer id, and a lead is exactly
               the thing that does not have one -- so an advisor could see
               notes ABOUT a call and not the conversation itself. -->
          <div class="leadtalk" id="leadtalk"></div>
          ${l.converted_job_id ? `<div class="kv"><span>Job</span><b><a href="#/job/${l.converted_job_id}">Open the job</a></b></div>` : ""}

          ${l.converted_job_id || shopWords().caseFile || isPpiMirror(l) ? "" : `
          <!-- THE CAR. Three unlabelled boxes jammed in a row was the old
               shape, and on a phone the year box and the make box touched.
               Now every field is labelled and has room, and the VIN goes
               first because one paste fills the other three: a technician
               reading a door jamb should type seventeen characters once,
               not a year and a make and a model they then get wrong. -->
          <div class="tocar">
            <div class="tocar-h">The ${esc(shopWords().thing)}</div>
            <p class="quiet">The price comes off the ${esc(shopWords().thing)}, so it is worth
              getting now. You can leave it blank and add it on the job.</p>

            <label class="tocar-f wide"><span>${esc(cap(shopWords().serial))}</span>
              <span class="tocar-vin">
                <input id="c-vin" autocapitalize="characters" spellcheck="false"
                       ${shopWords().hasEngine ? 'maxlength="17"' : ""}>
                ${/* Decoding is a VIN lookup. Nothing answers for a door
                      serial, so the button is not offered rather than
                      offered and broken. */ shopWords().hasEngine
                  ? `<button type="button" class="btn sm" id="c-decode">Decode</button>` : ""}
              </span></label>
            <div id="c-vinout"></div>

            <div class="tocar-row">
              <label class="tocar-f"><span>${esc(shopWords().yearLabel)}</span>
                <input id="c-year" inputmode="numeric" maxlength="4"></label>
              <label class="tocar-f"><span>${esc(shopWords().makeLabel)}</span>
                <input id="c-make"></label>
              <label class="tocar-f"><span>${esc(shopWords().modelLabel)}</span>
                <input id="c-model"></label>
            </div>
            <!-- COLOR. A replacement panel has to match the house, so
                 this is asked for at the lead, not discovered on site. -->
            ${shopWords().hasColour ? `<label class="tocar-f wide"><span>${esc(shopWords().colourLabel)}</span>
              <input id="c-colour"></label>` : ""}
            <!-- A door has no plate. The field is not offered rather than
                 offered and meaningless. -->
            ${shopWords().hasPlate ? `<label class="tocar-f wide"><span>Plate</span>
              <input id="c-plate" autocapitalize="characters"></label>` : ""}
          </div>`}

          <!-- THE PHOTOS, at the bottom and shut. Anthony: "show photo
               tile on lead/quote/job everywhere but folded in until job
               starts near bottom of card."

               Only once a lead has a job on it, because photos are filed
               against a job and a lead with nothing raised has nowhere to
               put them. The summary carries the count, so a shut fold
               still says whether it is worth opening. -->
          ${l.converted_job_id
            ? `<div class="leadphotos" id="leadphotos"></div>` : ""}
        </div>

        <div class="sheet-acts">
          <!-- SEND AGREEMENT, FIRST, ON A CASE FILE. Neelum, 2026-10-14: "I'm
               in the lead sheet and do not see any of that", then her list:
               name and number, the recording, the transcript, and a SEND
               AGREEMENT button. The first three were already on this sheet.
               The agreement is filed against a case, so the button opens one
               (or the one already raised) straight onto the agreement card. -->
          ${isPpiMirror(l) ? `<a class="btn primary" id="open-ppi" href="#" target="_blank" rel="noopener">Open in PPI Desk</a>
             ${l.customer_phone ? `<a class="btn" href="tel:${esc(l.customer_phone)}">Call them</a>` : ""}`
          : `${shopWords().caseFile
            ? `<button class="btn primary" id="to-agreement">Send agreement</button>
               ${l.converted_job_id
                 ? `<a class="btn" href="#/job/${l.converted_job_id}">Open the ${esc(shopWords().thing)}</a>`
                 : `<button class="btn" id="to-quote">Raise a quote</button>`}`
            : l.converted_job_id
            ? `<a class="btn primary" href="#/job/${l.converted_job_id}">Open the quote</a>`
            : `<button class="btn primary" id="to-quote">Raise a quote</button>
               <button class="btn" id="to-draft">Draft it for me</button>`}
          ${l.customer_phone ? `<a class="btn" href="tel:${esc(l.customer_phone)}">Call them</a>` : ""}
          <!-- AN INSPECTION GOES TO THE PPI DESK (Anthony 2026-10-14, "no
               overlap"). Drawn only on Supercanic, after showSheet checks. -->
          ${isPpiJob({ title: l.service_wanted, complaint: l.notes })
            ? `<span id="ppi-slot"></span>` : ""}
          <!-- CLEAR THE RED BY HAND. It comes off on its own when somebody
               rings them or they write back, which covers nearly every
               case -- and nearly is the problem. A tech who rang from a
               personal phone, a call the tracking missed, somebody sorted
               in the doorway: none of those reach the ledger. Only offered
               when there IS something to clear. -->
          <!-- SPAM IS NOT THE SAME AS LOST, even though both close the
               lead. Lost is a real person we failed to win. Spam never was
               one, and counting the two together makes the lost pile read
               worse than the shop deserves and the conversion rate read
               lower than it is. Same word as the arrival filter uses so a
               caught one and a hand-marked one count together. -->
          ${l.converted_job_id ? "" :
            `<button class="btn ghost danger" id="to-spam">Not a real lead</button>`}
          ${l.flag ? `<button class="btn ghost" id="clear-flag">Somebody dealt with this</button>` : ""}
          ${pile !== "booked" ? `<button class="btn" id="to-booked">Mark booked</button>` : ""}
          ${pile !== "lost" ? `<button class="btn ghost" id="to-lost">Mark lost</button>` : ""}`}
          ${pile === "lost" ? `<button class="btn ghost" id="to-waiting">Back to waiting</button>` : ""}
        </div>
        <div class="msg-slot" id="sheet-msg"></div>
      </div>
    </div>`;
}

/* Held the way curSpan is, because the sheet is opened from a click
   handler that has no argument to carry it. */
let myRole: Role = "viewer";

/**
 * ─── THE SCREEN KEEPS UP WITH THE PHONE ────────────────────────────────
 *
 * Anthony, 2026-09-10: "doesn't seem leads update when we text back or
 * update when a new one comes in very quick."
 *
 * Nothing was wrong behind it. callrail-intake polls every two minutes and
 * has run 720 times in the last day without a failure; the newest lead was
 * two minutes old when this was written. The screen simply never asked
 * again. It drew once, and a lead arriving thirty seconds later appeared
 * only if somebody reloaded -- and the stage a lead moves to when we text
 * back is worked out at READ time from the conversation, so that never
 * moved either.
 *
 * ─── AND IT DOES NOT INTERRUPT ─────────────────────────────────────────
 *
 * Anthony on the notifications panel: "It keeps popping every time you
 * change a page and is super annoying af." A list that redraws under
 * somebody's thumb is the same mistake. So it holds off while a lead sheet
 * is open, while the find box is being used, and while the tab is not on
 * screen -- and it refreshes the moment somebody comes back to the tab,
 * which is the one time they actually want the newest thing.
 */
let live: (() => void) | undefined;
let onShow: (() => void) | undefined;
const EVERY = 20_000;

/** Somebody is in the middle of something this must not disturb. */
function busy(): boolean {
  if (document.getElementById("sheet-back")) return true;      // a lead is open
  const find = document.querySelector<HTMLInputElement>("#findhost input");
  if (find && (find.value.trim() || document.activeElement === find)) return true;
  return document.visibilityState !== "visible";
}

/** Stopped whenever the screen is rebuilt or left, so it cannot pile up. */
export function stopLeadsLive(): void {
  if (live) { live(); live = undefined; }
  if (onShow) { document.removeEventListener("visibilitychange", onShow); onShow = undefined; }
}

function closeSheet(): void {
  document.getElementById("sheet-back")?.remove();
  open = null;
}

/**
 * THE CONVERSATION, ON THE LEAD CARD.
 *
 * Read oldest first, because this is a conversation rather than a log. A
 * call shows CallRail's own summary of it where there is one -- that is the
 * thing an advisor wants, and it is already paid for -- with the score and
 * the mood beside it, and a button to read the words if the summary is not
 * enough.
 *
 * It never blocks the card. The sheet is open and usable before this
 * finishes, and a failure says so in place rather than leaving an empty
 * box that looks like a customer who never said anything.
 */
async function fillTalk(phone: string | null, opts: { textBack?: boolean } = {}): Promise<void> {
  const host = document.getElementById("leadtalk");
  if (!host || !phone) return;
  host.innerHTML = `<div class="quiet">Reading the conversation&hellip;</div>`;
  let rows: Comm[] = [];
  try {
    rows = await commsForPhone(phone);
  } catch (e) {
    host.innerHTML = `<p class="msg bad">Could not read the conversation: ${
      esc(e instanceof Error ? e.message : String(e))}</p>`;
    return;
  }
  if (!rows.length) {
    host.innerHTML = `<div class="talk-h">Conversation</div>
      <div class="quiet">Nothing said either way yet.</div>`;
    return;
  }

  const bubble = (c: Comm): string => {
    const mine = c.direction === "outbound";
    const when = `${since(c.occurred_at)}`;
    if (c.kind === "call") {
      const said = String(c.raw?.call_summary ?? "").trim();
      const score = c.raw?.lead_score;
      return `<div class="talk-call">
        <div class="talk-call-h">
          <b>${esc(c.subject ?? "Call")}</b>
          ${c.duration_seconds ? `<span>${esc(spoken(c.duration_seconds))}</span>` : ""}
          ${typeof score === "number" ? `<span class="talk-score">lead ${score}</span>` : ""}
          ${c.raw?.sentiment ? `<span>${esc(String(c.raw.sentiment).toLowerCase())}</span>` : ""}
          <span class="talk-when">${esc(when)}</span>
        </div>
        ${said ? `<div class="talk-sum">${esc(said)}</div>` : ""}
        ${callButtonsHtml(c.raw?.callrail_id ? String(c.raw.callrail_id) : null)}
      </div>`;
    }
    return `<div class="talk-b ${mine ? "mine" : "theirs"}">
      <div class="talk-t">${esc(String(c.body ?? "").trim() || "(nothing)")}</div>
      <div class="talk-when">${mine ? "us" : "them"} &middot; ${esc(when)}</div>
    </div>`;
  };

  const texts = rows.filter((r) => r.kind === "sms").length;
  // Where to answer it. Only once something has been said: the panel
  // refuses to open a conversation with a number it has never talked to.
  /* Not on a PPI lead: its texts go from the PPI line, in the box above. */
  const jump = opts.textBack === false ? "" : await textBackLink(phone);
  host.innerHTML = `<div class="talk-h">Conversation
      <span class="quiet">${texts} text${texts === 1 ? "" : "s"}, ${
        rows.length - texts} call${rows.length - texts === 1 ? "" : "s"}</span></div>
    <div class="talk">${rows.map(bubble).join("")}</div>${jump}`;

  // Play and Read. One listener for every screen; see ui/callplay.ts.
  wireCallButtons();
}

function showSheet(l: Lead): void {
  closeSheet();
  open = l;
  document.body.insertAdjacentHTML("beforeend", panel(l));

  const done = () => { closeSheet(); void leadsPage(curSpan); };
  const fail = (e: unknown) => {
    const m = document.getElementById("sheet-msg");
    if (m) m.innerHTML = `<p class="msg bad">${esc(e instanceof Error ? e.message : String(e))}</p>`;
  };

  if (document.getElementById("ppi-slot")) {
    void maySendToPpiDesk().then((yes) => {
      const slot = document.getElementById("ppi-slot");
      if (!yes || !slot) return;
      slot.outerHTML = `<button class="btn" id="to-ppidesk">Send to PPI Desk</button>`;
      wireSendToPpiDesk(document.getElementById("to-ppidesk") as HTMLButtonElement | null,
        { enquiry_id: l.id, ...(l.converted_job_id ? { job_id: l.converted_job_id } : {}) },
        (t, bad) => {
          const m = document.getElementById("sheet-msg");
          if (m) m.innerHTML = `<p class="msg${bad ? " bad" : ""}">${esc(t)}</p>`;
        });
    });
  }

  if (l.converted_job_id) {
    jobPhotos("leadphotos", l.converted_job_id, null, myRole, "damage",
      { shut: true });
  }

  /* A note typed here is a note about the LEAD. Once it becomes a job the
     notes go with it (see convertToQuote), so nothing written before the
     work existed is left behind on a card nobody opens again. */
  void notesCard("lead-notes", { enquiryId: l.id }, myRole, {
    title: "Notes", blurb: "What they said, and anything worth remembering",
    ...(l.converted_job_id ? { show: { jobId: l.converted_job_id } } : {}) });

    void fillTalk(l.customer_phone, { textBack: !isPpiMirror(l) });
  if (isPpiMirror(l)) void fillPpiMirror(l);

  document.getElementById("sheet-x")?.addEventListener("click", closeSheet);
  document.getElementById("sheet-back")?.addEventListener("click", (e) => {
    if ((e.target as HTMLElement).id === "sheet-back") closeSheet();
  });

  /**
   * GOING SOMEWHERE CLOSES THE SHEET.
   *
   * Anthony, from a phone: "open quote doesn't work from here on mobile."
   *
   * It worked. The sheet is appended to document.body rather than into the
   * page, so when the link changed the hash the router drew the job
   * underneath and the sheet stayed exactly where it was, covering it. On
   * a desktop the sheet is a centered dialog and you can see the page move
   * behind it, so it reads as working. On a phone it is a full-width
   * bottom sheet with nothing visible around it, so the screen simply does
   * not change and the button looks dead.
   *
   * A hashchange listener rather than a click handler on each link,
   * because there are four ways out of this sheet -- the quote, the job,
   * the customer's record, and the browser's own back button -- and three
   * of them would have been missed.
   */
  window.addEventListener("hashchange", function leaveSheet() {
    closeSheet();
    window.removeEventListener("hashchange", leaveSheet);
  });
  document.addEventListener("keydown", function esc2(e) {
    if (e.key === "Escape") { closeSheet(); document.removeEventListener("keydown", esc2); }
  });

  /**
   * DECODE THE VIN AND FILL THE REST IN.
   *
   * Seventeen characters typed once beats a year, a make and a model typed
   * three times and mistyped twice — and the vehicle is what the price
   * comes off, so a wrong model is a wrong quote. Anything already typed
   * is left alone: a person who filled the make in by hand meant it.
   */
  document.getElementById("c-decode")?.addEventListener("click", async () => {
    const b = document.getElementById("c-decode") as HTMLButtonElement;
    const box = document.getElementById("c-vin") as HTMLInputElement | null;
    const out = document.getElementById("c-vinout");
    const vin = (box?.value ?? "").trim().toUpperCase();
    if (!out) return;
    if (vin.length !== 17) {
      out.innerHTML = `<p class="msg bad">A VIN is 17 characters. That one is ${vin.length}.</p>`;
      return;
    }
    b.disabled = true; b.textContent = "Reading…";
    try {
      const r = await fetch(api(`/api/vin?vin=${encodeURIComponent(vin)}`));
      const d = await r.json() as Record<string, string | number | null>;
      b.disabled = false; b.textContent = "Decode";
      if (!r.ok) {
        out.innerHTML = `<p class="msg bad">${esc(String(d["error"] ?? "That did not decode."))}</p>`;
        return;
      }
      const put = (id: string, v: unknown) => {
        const el = document.getElementById(id) as HTMLInputElement | null;
        if (el && !el.value.trim() && v) el.value = String(v);
      };
      put("c-year", d["year"]); put("c-make", d["make"]); put("c-model", d["model"]);
      out.innerHTML = vinOutHtml(d);
    } catch (e) {
      b.disabled = false; b.textContent = "Decode";
      out.innerHTML = `<p class="msg bad">${esc(e instanceof Error ? e.message : String(e))}</p>`;
    }
  });

  document.getElementById("to-agreement")?.addEventListener("click", async (ev) => {
    const b = ev.currentTarget as HTMLButtonElement;
    b.disabled = true; b.textContent = "Opening it…";
    try {
      const id = l.converted_job_id ?? await convertToQuote(l);
      /* esign-card.ts reads this once and scrolls the agreement into view. */
      try { sessionStorage.setItem("esign:focus", id); } catch { /* private mode: it still opens */ }
      closeSheet();
      location.hash = `#/job/${id}`;
    } catch (e) {
      b.disabled = false; b.textContent = "Send agreement";
      fail(e);
    }
  });

  // The advisor's actual first move of the day: lead in, quote out.
  document.getElementById("to-quote")?.addEventListener("click", async (ev) => {
    const b = ev.currentTarget as HTMLButtonElement;
    b.disabled = true; b.textContent = "Raising it…";
    const val = (id: string) => (document.getElementById(id) as HTMLInputElement | null)?.value.trim() ?? "";
    try {
      const yr = Number(val("c-year"));
      const id = await convertToQuote(l, {
        year: yr >= 1900 && yr <= 2100 ? yr : null,
        make: val("c-make"), model: val("c-model"),
        plate: val("c-plate"), vin: val("c-vin").toUpperCase(),
        colour: val("c-colour"),
      });
      closeSheet();
      location.hash = `#/job/${id}`;
    } catch (e) {
      b.disabled = false; b.textContent = "Raise a quote";
      fail(e);
    }
  });

  /**
   * DRAFT IT FOR ME.
   *
   * Anthony: "why are leads like darrell not getting an auto generated
   * quote for someone to review and send."
   *
   * Because the thing that used to do it was a cron on the old system, and
   * that is switched off. This is the same work done on demand instead,
   * which is the better shape: a sweep that drafts quotes for every lead
   * every three minutes produces a pile nobody reads, and it drafts
   * against leads that are half a sentence long. Pressing the button means
   * somebody has looked at the lead and thinks it is worth pricing.
   *
   * It raises the job, asks for book time, and prices the labor at the
   * shop's rate. It does NOT send anything — an advisor reads it, fixes
   * it, and decides.
   */
  document.getElementById("to-draft")?.addEventListener("click", async (ev) => {
    const b = ev.currentTarget as HTMLButtonElement;
    const val = (id: string) =>
      (document.getElementById(id) as HTMLInputElement | null)?.value.trim() ?? "";
    b.disabled = true; b.textContent = "Drafting…";
    try {
      const yr = Number(val("c-year"));
      const jobId = await convertToQuote(l, {
        year: yr >= 1900 && yr <= 2100 ? yr : null,
        make: val("c-make"), model: val("c-model"),
        plate: val("c-plate"), vin: val("c-vin").toUpperCase(),
      });

      // The lead's own words are the best description of the fault there
      // is; the car may only be in them too.
      const said = [l.service_wanted, l.notes].filter(Boolean).join(". ");
      const vehicle = [val("c-year"), val("c-make"), val("c-model")].filter(Boolean).join(" ")
        || said.split(/[.,\n]/)[0] || "";

      const sb = await db();
      const { data: { session } } = await sb.auth.getSession();
      const res = await fetch(api("/api/labour"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token ?? ""}`,
        },
        body: JSON.stringify({ vehicle, complaint: said }),
      });

      // A draft that could not be priced is still a job worth opening —
      // the advisor lands on it and types the lines themselves.
      if (res.ok) {
        const body = await res.json() as { lines?: Array<{ task: string; hours: number }> };
        const rate = await shopLabourRate();
        for (const line of (body.lines ?? []).slice(0, 8)) {
          await saveLine(jobId, {
            kind: "labour", description: line.task, quantity: line.hours,
            unit_price: rate, cost: null,
          });
        }
      }

      closeSheet();
      location.hash = `#/job/${jobId}`;
    } catch (e) {
      b.disabled = false; b.textContent = "Draft it for me";
      fail(e);
    }
  });

  document.getElementById("clear-flag")?.addEventListener("click", async () => {
    const b = document.getElementById("clear-flag") as HTMLButtonElement;
    const m = document.getElementById("sheet-msg");
    b.disabled = true; b.textContent = "Clearing…";
    try {
      const n = await clearFlagFor(open?.customer_phone ?? "");
      if (m) {
        m.innerHTML = `<p class="msg ok">Cleared${
          n > 1 ? ` ${n} alerts` : ""}. It will not come back for this number.</p>`;
      }
      void leadsPage(curSpan);
    } catch (e) {
      b.disabled = false; b.textContent = "Somebody dealt with this";
      if (m) {
        m.innerHTML = `<p class="msg bad">${
          esc(e instanceof Error ? e.message : String(e))}</p>`;
      }
    }
  });

  document.getElementById("to-booked")?.addEventListener("click", () =>
    setLeadStatus(l.id, "booked").then(done).catch(fail));
  document.getElementById("to-lost")?.addEventListener("click", () =>
    setLeadStatus(l.id, "lost", hasAgedOut(l)
      ? `No answer within ${AGED_DAYS} days and no quote raised.` : "Closed by the desk.")
      .then(done).catch(fail));
  document.getElementById("to-waiting")?.addEventListener("click", () =>
    setLeadStatus(l.id, "new").then(done).catch(fail));

  /* Two taps, because "not a real lead" is a judgment about a person who
     may simply have written badly, and the button sits next to the ones
     that close a real enquiry. */
  document.getElementById("to-spam")?.addEventListener("click", () => {
    const b = document.getElementById("to-spam") as HTMLButtonElement;
    if (b.dataset["sure"] !== "1") {
      b.dataset["sure"] = "1";
      b.textContent = "Sure? It closes the lead";
      setTimeout(() => {
        if (b.dataset["sure"] === "1") {
          b.dataset["sure"] = ""; b.textContent = "Not a real lead";
        }
      }, 4000);
      return;
    }
    b.disabled = true; b.textContent = "Filing…";
    setLeadStatus(l.id, "lost", SPAM_REASON).then(done).catch(fail);
  });
}

let curSpan: string | undefined;

/**
 * A RANGE ON THE PILES THAT ARE RECORDS.
 *
 * Waiting does not get one. It is this morning's queue, its whole point is
 * that it is short, and a date filter on it is a way to hide work.
 *
 * Empty strings mean "no bound", which is also what the date inputs hand
 * back when they are cleared, so there is nothing to translate.
 */
const RANGED: Pile[] = ["booked", "lost"];
let fromDay = "";
let toDay = "";

/** yyyy-mm-dd from a date box to the instant the day starts, or ends. */
const dayStart = (d: string): string | null =>
  d ? new Date(`${d}T00:00:00`).toISOString() : null;
const dayEnd = (d: string): string | null =>
  d ? new Date(`${d}T23:59:59.999`).toISOString() : null;

/** What the heading says the list is holding. */
function rangeWords(): string {
  const say = (d: string) => new Date(`${d}T12:00:00`)
    .toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  if (fromDay && toDay) return `${say(fromDay)} to ${say(toDay)}`;
  if (fromDay) return `since ${say(fromDay)}`;
  if (toDay) return `up to ${say(toDay)}`;
  return "";
}

export async function leadsPage(span?: string, role: Role = "viewer"): Promise<void> {
  /* Whatever timer the last draw left, it belongs to a screen that is
     about to be replaced. */
  stopLeadsLive();
  myRole = role;
  curSpan = span;
  const sp = SPANS.find((x) => x.key === span);
  /* A range typed into the boxes beats the railway's span, because it was
     typed. Clearing both boxes hands the screen back to the span. */
  const ranged = RANGED.includes(pile) && Boolean(fromDay || toDay);
  const since = ranged ? dayStart(fromDay) : sp ? spanFrom(sp.key) : null;
  const until = ranged ? dayEnd(toDay) : null;

  mount(`<div class="page">
      <header class="page-head">
        ${sp ? `<a class="back" href="#/">&larr; Desk</a>` : ""}
        <h1>Leads</h1>
        <p>${sp && sp.key !== "all"
          ? `From the railway &mdash; ${esc(sp.label.toLowerCase())}.`
          : "Waiting is oldest first &mdash; the top of it is the person most likely to be gone already."}</p>
        <!-- There was no way to write one down. Every lead in here arrived
             through a form, a call or a bridge, and somebody standing at
             the counter had nowhere to put a name. -->
        <button class="btn primary newjob" id="newlead">New lead</button></header>
      <div class="tabs wide" id="lead-tabs"></div>
      <div id="lead-tools"></div>
      <div id="findhost">${findBarHtml("Find a lead — name, number, what they want")}</div>
      <div id="lead-quiet"></div>
      <div class="leadgrid" id="lead-grid"><div class="loading">Reading&hellip;</div></div>
    </div>`);

  try {
    const [counts, leads, zone] = await Promise.all([
      countPiles(), listLeads(pile, since, until), shopTimezone()]);
    shopZone = zone;

    const tabs = document.getElementById("lead-tabs");
    if (tabs) {
      tabs.innerHTML = PILES.map((p) =>
        `<button class="tab ${p.key === pile ? "on" : ""}" data-p="${p.key}">${esc(p.label)}
          <i>${counts[p.key]}</i></button>`).join("");
      tabs.querySelectorAll<HTMLButtonElement>(".tab").forEach((b) =>
        b.addEventListener("click", () => {
          pile = (b.dataset["p"] as Pile) ?? "waiting";
          openedUp = false;
          /* Waiting has no range, so leaving one set while you look at it
             and finding it still applied on the way back is a trap. */
          if (!RANGED.includes(pile)) { fromDay = ""; toDay = ""; }
          void leadsPage(curSpan);
        }));
    }

    // Only offered where it applies, and it says the number before it acts.
    const tools = document.getElementById("lead-tools");
    if (tools) {
      const stale = leads.filter(hasAgedOut).length;
      /* THE RANGE, on the two piles that are records. It sits above the
         aged-out tool because it changes what that tool is counting. */
      tools.innerHTML = (RANGED.includes(pile)
        ? `<div class="listbar rangebar">
             <span class="pager-note">${ranged
               ? `${leads.length} ${esc(pile)} &mdash; ${esc(rangeWords())}, by the day the lead came in.`
               : `${leads.length} ${esc(pile)}. Pick any two dates to look at a period.`}</span>
             <span class="rangepick">
               <label>From <input type="date" id="lead-from" value="${esc(fromDay)}"></label>
               <label>To <input type="date" id="lead-to" value="${esc(toDay)}"></label>
               ${ranged ? `<button class="btn sm ghost" id="lead-clear">Clear</button>` : ""}
             </span>
           </div>`
        : "")
        + (pile === "lost" && stale
        ? `<div class="listbar"><span class="pager-note">${stale} of these aged out on their own and
             are still marked open in the record.</span>
           <button class="btn sm" id="close-aged">Close them properly</button></div>`
        : "");

      const setRange = (which: "from" | "to") => (e: Event) => {
        const v = (e.target as HTMLInputElement).value;
        if (which === "from") fromDay = v; else toDay = v;
        openedUp = false;
        void leadsPage(curSpan);
      };
      document.getElementById("lead-from")?.addEventListener("change", setRange("from"));
      document.getElementById("lead-to")?.addEventListener("change", setRange("to"));
      document.getElementById("lead-clear")?.addEventListener("click", () => {
        fromDay = ""; toDay = ""; openedUp = false;
        void leadsPage(curSpan);
      });
      document.getElementById("close-aged")?.addEventListener("click", async () => {
        const b = document.getElementById("close-aged") as HTMLButtonElement | null;
        if (b) { b.disabled = true; b.textContent = "Closing…"; }
        try { await closeAgedOut(); void leadsPage(curSpan); }
        catch { if (b) { b.disabled = false; b.textContent = "Close them properly"; } }
      });
    }

    const grid = document.getElementById("lead-grid");
    if (!grid) return;

    /**
     * A WEEK, AND THE REST BEHIND A BUTTON.
     *
     * Anthony: "my staff only needs to see the data from the last week
     * anywhere. Anything older needs to be behind load more."
     *
     * A lead older than a week has either been dealt with or has already
     * rung somebody else, and either way it is not what the person opening
     * this at eight in the morning is looking for. The count of what is
     * older is on the button rather than left to be discovered, because a
     * list that quietly stops short is how a lead goes missing, which is
     * the thing this whole screen exists to prevent.
     *
     * Anything the feed has flagged stays on screen whatever its age. A
     * landline that rang nine days ago and has still never been rung back
     * is exactly the row that must not fall off the bottom.
     */
    const WEEK = 7 * 864e5;
    /* A range you typed is not folded behind a week. Asking for June and
       being told "nothing in the last week" is the screen ignoring you. */
    const fresh = (l: Lead) =>
      ranged
      || Boolean(l.flag)
      || Date.parse(l.moved_at ?? l.created_at) >= Date.now() - WEEK;

    const draw = (rows: Lead[]): void => {
      const older = rows.filter((l) => !fresh(l)).length;
      const show = openedUp ? rows : rows.filter(fresh);
      grid.innerHTML = (show.length
        ? show.map(card).join("")
        : `<div class="card"><div class="empty">${ranged
            ? `No ${esc(pile)} leads ${esc(rangeWords())}.`
            : `Nothing in the last week ${
            pile === "waiting" ? "waiting" : pile === "booked" ? "booked from a lead" : "here"
          }.`} <span>&#127942;</span></div></div>`)
        + (older && !openedUp
          ? `<button class="minemore" id="leadmore">Show ${older} older
               ${older === 1 ? "lead" : "leads"}</button>`
          : "");
      document.getElementById("leadmore")?.addEventListener("click", () => {
        openedUp = true;
        draw(rows);
      });
      grid.querySelectorAll<HTMLButtonElement>(".lead").forEach((b) =>
        b.addEventListener("click", () => {
          const l = rows.find((x) => x.id === b.dataset["id"]);
          if (l) showSheet(l);
        }));
    };
    // Newest on top is the right order for a morning, but it does mean an
    // old lead nobody rang sinks. So the count of those is stated, with a
    // way to see only them — the order stays, the risk does not.
    const paintBanner = (rows: Lead[]): void => {
      const banner = document.getElementById("lead-quiet");
      if (!banner) return;
      const quiet = rows.filter(goneQuiet);
      banner.innerHTML = pile === "waiting" && quiet.length
        ? `<div class="quietnote">${quiet.length} of these ${quiet.length === 1 ? "has" : "have"}
             had no contact for more than two days.
             <button class="linkbtn" id="only-quiet">Show just those</button>
             <button class="linkbtn" id="all-again" hidden>Show all again</button></div>`
        : "";
      document.getElementById("only-quiet")?.addEventListener("click", () => {
        draw(quiet);
        document.getElementById("only-quiet")?.setAttribute("hidden", "");
        document.getElementById("all-again")?.removeAttribute("hidden");
      });
      document.getElementById("all-again")?.addEventListener("click", () => {
        draw(rows);
        document.getElementById("all-again")?.setAttribute("hidden", "");
        document.getElementById("only-quiet")?.removeAttribute("hidden");
      });
    };

    draw(leads);
    paintBanner(leads);
    document.getElementById("newlead")?.addEventListener("click", () =>
    newJobSheet(() => void leadsPage(span), "lead"));

    /* ─── KEEP IT CURRENT, WITHOUT REBUILDING THE SCREEN ──────────────
       This used to re-run leadsPage on every tick and every socket event.
       Two things were wrong with that. The screen was torn back to its
       "Reading…" skeleton and repainted, which is a visible flash and
       loses where somebody was; and re-running leadsPage RE-SUBSCRIBED,
       which reset the sweep clock, so the twenty second interval below
       never once elapsed and the whole thing ran at the five second tick
       rate instead. That is the flicker.

       So the live refresh now refetches and repaints the rows in place —
       the counts, the list, the quiet banner — and leaves the skeleton,
       the date pickers, the find box and the subscription alone. The
       channel is opened once per visit to the screen, the way the comms
       panel and the feed already do it. See busy() and stopLeadsLive(). */
    const quietReload = async (): Promise<void> => {
      if (busy()) return;
      if (!document.getElementById("lead-grid")) return;
      let fresh: Lead[];
      let freshCounts: Record<Pile, number>;
      try { [freshCounts, fresh] = await Promise.all([
        countPiles(), listLeads(pile, since, until)]); }
      catch { return; }   // a blip on a background refresh is not worth a red box
      // Navigated away, or somebody started reading, while the read was out.
      if (busy() || !document.getElementById("lead-grid")) return;
      document.querySelectorAll<HTMLElement>("#lead-tabs .tab").forEach((t) => {
        const k = t.dataset["p"] as Pile | undefined;
        const n = t.querySelector("i");
        if (k && n) n.textContent = String(freshCounts[k]);
      });
      draw(fresh);
      paintBanner(fresh);
    };

    stopLeadsLive();
    live = startLive({
      channel: "leads",
      table: "enquiries",
      refresh: quietReload,
      alive: () => !!document.getElementById("lead-grid"),
      fastMs: EVERY,
    });
    /* Coming back to the tab is the one moment somebody definitely wants
       the newest thing, so that does not wait for the next tick. */
    onShow = () => { void quietReload(); };
    document.addEventListener("visibilitychange", onShow);

  wireFindBar({ hostId: "findhost", listId: "lead-grid", noun: "lead" });
  } catch (e) {
    const grid = document.getElementById("lead-grid");
    if (grid) grid.innerHTML = `<div class="card"><div class="empty bad">${
      esc(e instanceof Error ? e.message : String(e))}</div></div>`;
  }
}

/* ─────────────── the PPI desk request behind a 'ppi-desk' lead ─────────────── */

async function bridge(path: string, body?: unknown): Promise<Response> {
  const sb = await db();
  const { data: { session } } = await sb.auth.getSession();
  return fetch(api(`/api/ppidesk/bridge/${path}`), {
    method: body ? "POST" : "GET",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${session?.access_token ?? ""}` },
    body: body ? JSON.stringify(body) : undefined,
  });
}

type Bridge = {
  request: { id: string; code: string; step: string; step_label: string; price: number | null; paid: boolean;
    scheduled_for: string | null; vehicle: string };
  link: string; working: { id: string; name: string } | null; me: { id: string; name: string };
  texts: Array<{ at: string; way: "in" | "out"; body: string; by: string | null; status: string | null }>;
};

async function fillPpiMirror(l: Lead): Promise<void> {
  const host = document.getElementById("ppi-mirror");
  if (!host) return;
  const r = await bridge(encodeURIComponent(l.id)).catch(() => null);
  const d = r?.ok ? await r.json() as Bridge : null;
  if (!d) {
    host.innerHTML = `<p class="msg bad">${r?.status === 403 ? "Only the desk team can work PPI leads."
      : "The PPI desk did not answer. Open it there."}</p>`;
    return;
  }
  const open = document.getElementById("open-ppi") as HTMLAnchorElement | null;
  if (open) open.href = d.link;
  const q = d.request;
  host.innerHTML = `<div class="card ppi-mirror-head"><div class="pad">
      <b>PPI Desk ${esc(q.code)} &middot; ${esc(q.step_label)}</b>
      <span class="quiet">${esc(q.vehicle === "the vehicle" ? "car not given yet" : q.vehicle)}${
        q.price != null ? ` &middot; $${esc(q.price)}${q.paid ? " paid" : ""}` : ""}${
        q.scheduled_for ? ` &middot; ${esc(new Date(q.scheduled_for).toLocaleString("en-US",
          { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }))}` : ""}</span>
    </div></div>
    ${workingBar(d.working, d.me.id)}
    ${textsCard(d.texts, { buyer_phone: l.customer_phone, buyer_name: l.customer_name }, "lead-ppi-text")}
    <p class="msg" id="ppi-mirror-msg"></p>`;
  const say = (t: string, bad = false) => {
    const m = document.getElementById("ppi-mirror-msg");
    if (m) { m.textContent = t; m.className = `msg${bad ? " bad" : ""}`; }
  };
  const again = () => void fillPpiMirror(l);
  wireWorking((b) => bridge(`${encodeURIComponent(l.id)}/claim`, b), say, again);
  host.querySelector<HTMLButtonElement>('[data-do="text"]')?.addEventListener("click", async (e) => {
    const btn = e.currentTarget as HTMLButtonElement;
    const box = document.getElementById("lead-ppi-text") as HTMLTextAreaElement | null;
    const text = box?.value.trim() ?? "";
    if (!text) return say("Write the text first.", true);
    btn.disabled = true;
    const res = await bridge(`${encodeURIComponent(l.id)}/text`, { text });
    const j = await res.json().catch(() => ({})) as { error?: string };
    if (!res.ok) { btn.disabled = false; return say(j.error ?? "Not sent.", true); }
    say("Sent.");
    setTimeout(again, 700);
  });
  const thread = host.querySelector(".ppi-thread");
  if (thread) thread.scrollTop = thread.scrollHeight;
}
